import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../../../shared/contexts/OrgContext';
import { CaptureItem, Video } from '../types';
import { VideoProcessingJob, videoProcessingJobsService } from '../../../shared/services/videoProcessingJobsService';
import { jobMonitoringService } from '../../../shared/services/jobMonitoringService';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { supabase } from '../../../shared/lib/supabase';
import { matchService } from '../../../shared/services/matches';

interface VideoDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  video: Video | null;
  asset?: CaptureItem | null;
  onToast?: (message: string, type: 'success' | 'error') => void;
  onDeleteVideo?: (videoId: string, videoName: string, storagePath: string) => void;
}

const VideoDetailsDrawer: React.FC<VideoDetailsDrawerProps> = ({
  isOpen,
  onClose,
  video,
  asset,
  onToast,
  onDeleteVideo,
}) => {
  const navigate = useNavigate();
  const { currentOrganization } = useOrg();
  const location = window.location;
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  const orgId = currentOrganization?.id || orgIdFromPath || 'default';
  
  const [jobs, setJobs] = useState<VideoProcessingJob[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [linkedMatch, setLinkedMatch] = useState<{ id: string; name: string } | null>(null);
  const [loadingMatch, setLoadingMatch] = useState(false);
  const isAssetView = Boolean(asset && asset.assetKind && asset.assetKind !== 'master');
  const activeVideo = asset?.video || video;

  // Load match link for video
  useEffect(() => {
    const loadMatchLink = async () => {
      if (!activeVideo || !isOpen || isAssetView) {
        setLinkedMatch(null);
        return;
      }

      setLoadingMatch(true);
      try {
        // Check if video is linked to a match via match_videos
        const { data: matchVideos, error: matchVideosError } = await supabase
          .from('match_videos')
          .select('match_id')
          .eq('storage_video_id', activeVideo.id)
          .limit(1);

        if (!matchVideosError && matchVideos && matchVideos.length > 0) {
          // Fetch match details
          const matchResult = await matchService.getMatchDetails(matchVideos[0].match_id);
          if (!matchResult.error && matchResult.data) {
            const match = matchResult.data;
            const homeTeam = (match.home_team as any)?.name || match.home_team_id || 'Home Team';
            const awayTeam = (match.away_team as any)?.name || match.away_team_id || 'Away Team';
            const matchDate = match.start_date ? new Date(match.start_date).toLocaleDateString() : '';
            setLinkedMatch({
              id: match.id,
              name: `${homeTeam} vs ${awayTeam}${matchDate ? ` - ${matchDate}` : ''}`,
            });
          } else {
            setLinkedMatch(null);
          }
        } else {
          // Check processing_notes for pending match link
          const processingNotes = activeVideo.processing_notes as Record<string, any> | null;
          const pendingMatchId = processingNotes?.match_id;
          if (pendingMatchId) {
            const matchResult = await matchService.getMatchDetails(pendingMatchId);
            if (!matchResult.error && matchResult.data) {
              const match = matchResult.data;
              const homeTeam = (match.home_team as any)?.name || match.home_team_id || 'Home Team';
              const awayTeam = (match.away_team as any)?.name || match.away_team_id || 'Away Team';
              const matchDate = match.start_date ? new Date(match.start_date).toLocaleDateString() : '';
              setLinkedMatch({
                id: match.id,
                name: `${homeTeam} vs ${awayTeam}${matchDate ? ` - ${matchDate}` : ''} (Pending)`,
              });
            } else {
              setLinkedMatch(null);
            }
          } else {
            setLinkedMatch(null);
          }
        }
      } catch (err) {
        console.error('Error loading match link:', err);
        setLinkedMatch(null);
      } finally {
        setLoadingMatch(false);
      }
    };

    loadMatchLink();
  }, [activeVideo, isAssetView, isOpen]);

  // Monitor jobs when video is open
  useEffect(() => {
    if (!activeVideo || !isOpen || isAssetView) return;

    // Load jobs initially
    loadJobs();

    // Start monitoring jobs
    const cleanup = jobMonitoringService.startMonitoring(
      activeVideo.id,
      (updatedJobs) => {
        setJobs(updatedJobs);

        // Check if all jobs are complete
        const allComplete = jobMonitoringService.areAllJobsComplete(updatedJobs);

        // Update video status based on job results
        if (allComplete && updatedJobs.length > 0) {
          // This will be handled by the worker, but we can show notifications
        }
      },
      3000 // Poll every 3 seconds
    );

    return cleanup;
  }, [activeVideo, isAssetView, isOpen]);

  const loadJobs = async () => {
    if (!activeVideo) return;
    setIsLoadingJobs(true);
    try {
      const result = await videoProcessingJobsService.getJobsByVideoId(activeVideo.id);
      if (result.success && result.data) {
        setJobs(result.data);
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setIsLoadingJobs(false);
    }
  };

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const formatFileSize = (bytes: number | null | undefined): string => {
    if (!bytes) return 'Unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return 'Unknown';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatResolution = (width: number | null, height: number | null): string => {
    if (!width || !height) return 'Unknown';
    return `${width}x${height}`;
  };

  const getJobStatusBadge = (status: string) => {
    const styles = {
      queued: 'bg-gray-100 text-gray-800 border border-gray-200',
      running: 'bg-blue-100 text-blue-800 border border-blue-200',
      succeeded: 'bg-green-100 text-green-800 border border-green-200',
      failed: 'bg-red-100 text-red-800 border border-red-200',
    };
    return styles[status as keyof typeof styles] || styles.queued;
  };

  /**
   * Check if all jobs have exhausted retries (attempts >= 5)
   */
  const hasExhaustedRetries = (jobList: VideoProcessingJob[]): boolean => {
    if (jobList.length === 0) return false;
    // Check if all failed jobs have attempts >= 5
    const failedJobs = jobList.filter(job => job.status === 'failed');
    if (failedJobs.length === 0) return false;
    return failedJobs.every(job => job.attempts >= 5);
  };

  /**
   * Check if there's any successful job
   */
  const hasSuccessfulJob = (jobList: VideoProcessingJob[]): boolean => {
    return jobList.some(job => job.status === 'succeeded');
  };

  /**
   * Get the latest job error message
   */
  const getLatestJobError = (jobList: VideoProcessingJob[]): string | null => {
    if (jobList.length === 0) return null;
    // Sort by created_at descending to get latest
    const sortedJobs = [...jobList].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const latestJob = sortedJobs[0];
    return latestJob.error_message || null;
  };

  /**
   * Check if upload section should be shown
   * Shows when:
   * 1. Status is "uploaded" (normal case), OR
   * 2. Status is "processing" or "error" AND:
   *    - All retries exhausted (attempts >= 5)
   *    - No successful job exists
   *    - Latest job error is "file not found"
   */
  const shouldShowUploadSection = (): boolean => {
    if (!activeVideo || isAssetView) return false;

    // Remote-link uploads (videos created from a source_url) should never show
    // the manual "Upload File" page. The worker is responsible for fetching
    // the file from the remote URL instead of the user uploading it.
    const processingNotes = activeVideo.processing_notes as Record<string, any> | null;
    if (processingNotes?.source_url) {
      return false;
    }

    // Normal case: status is "uploaded"
    if (activeVideo.status === 'uploaded') {
      return true;
    }

    // Special case: processing/error with exhausted retries and file not found
    if (activeVideo.status === 'processing' || activeVideo.status === 'error') {
      const exhaustedRetries = hasExhaustedRetries(jobs);
      const noSuccess = !hasSuccessfulJob(jobs);
      const latestError = getLatestJobError(jobs);
      const isFileNotFoundError = latestError 
        ? (latestError.toLowerCase().includes('file not found') || 
           latestError.toLowerCase().includes('no file found'))
        : false;

      return exhaustedRetries && noSuccess && isFileNotFoundError;
    }

    return false;
  };

  const handleRetryJob = async (jobId: string) => {
    const result = await videoProcessingJobsService.retryJob(jobId);
    if (result.success) {
      loadJobs();
    } else if (onToast) {
      onToast(result.error || 'Failed to retry job', 'error');
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    const result = await videoProcessingJobsService.deleteJob(jobId);
    if (result.success) {
      loadJobs();
      if (onToast) {
        onToast('Job deleted successfully', 'success');
      }
    } else if (onToast) {
      onToast(result.error || 'Failed to delete job', 'error');
    }
  };


  const formatTimestamp = (ms: number | null | undefined): string => {
    if (ms == null) return 'Unknown';
    const seconds = ms / 1000;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')} (${(seconds).toFixed(1)}s)`;
  };

  if (!isOpen || (!activeVideo && !asset)) return null;

  const jobSummary = jobMonitoringService.getJobSummary(jobs);
  const assetMetadata = asset?.assetMetadata;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isAssetView ? 'Asset Details' : 'Video Details'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {isAssetView ? asset?.name : activeVideo?.file_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Basic Info */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Basic Information</h3>
            <div className="space-y-3">
              {isAssetView ? (
                <>
                  <div>
                    <label className="text-xs text-gray-500">Asset Type</label>
                    <p className="text-sm text-gray-900 mt-1 capitalize">{asset?.assetKind}</p>
                  </div>
                  {asset?.assetMetadata?.timestampMs != null && (
                    <div>
                      <label className="text-xs text-gray-500">Timestamp</label>
                      <p className="text-sm text-gray-900 mt-1">
                        {formatTimestamp(asset.assetMetadata.timestampMs)}
                      </p>
                    </div>
                  )}
                  {assetMetadata?.sizeBytes && (
                    <div>
                      <label className="text-xs text-gray-500">File Size</label>
                      <p className="text-sm text-gray-900 mt-1">
                        {formatFileSize(assetMetadata.sizeBytes)}
                      </p>
                    </div>
                  )}
                  {assetMetadata?.width && assetMetadata?.height && (
                    <div>
                      <label className="text-xs text-gray-500">Resolution</label>
                      <p className="text-sm text-gray-900 mt-1">
                        {formatResolution(assetMetadata.width, assetMetadata.height)}
                      </p>
                    </div>
                  )}
                  {activeVideo && (
                    <div>
                      <label className="text-xs text-gray-500">Parent Video</label>
                      <p className="text-sm text-gray-900 mt-1">{activeVideo.file_name}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <label className="text-xs text-gray-500">File Name</label>
                    <p className="text-sm text-gray-900 mt-1">{activeVideo?.file_name}</p>
                  </div>
                  {activeVideo?.description && (
                    <div>
                      <label className="text-xs text-gray-500">Description</label>
                      <p className="text-sm text-gray-900 mt-1">{activeVideo.description}</p>
                    </div>
                  )}
                  {activeVideo && (
                    <div>
                      <label className="text-xs text-gray-500">Status</label>
                      <div className="mt-1">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          activeVideo.status === 'ready' ? 'bg-green-100 text-green-800 border border-green-200' :
                          activeVideo.status === 'processing' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                          activeVideo.status === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
                          'bg-gray-100 text-gray-800 border border-gray-200'
                        }`}>
                          {activeVideo.status.charAt(0).toUpperCase() + activeVideo.status.slice(1)}
                        </span>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-gray-500">File Size</label>
                    <p className="text-sm text-gray-900 mt-1">{formatFileSize(activeVideo?.size_bytes ?? null)}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Duration</label>
                    <p className="text-sm text-gray-900 mt-1">{formatDuration(activeVideo?.duration_seconds ?? null)}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Resolution</label>
                    <p className="text-sm text-gray-900 mt-1">{formatResolution(activeVideo?.width ?? null, activeVideo?.height ?? null)}</p>
                  </div>
                  {activeVideo?.codec && (
                    <div>
                      <label className="text-xs text-gray-500">Codec</label>
                      <p className="text-sm text-gray-900 mt-1">{activeVideo.codec}</p>
                    </div>
                  )}
                  {activeVideo?.bitrate && (
                    <div>
                      <label className="text-xs text-gray-500">Bitrate</label>
                      <p className="text-sm text-gray-900 mt-1">{activeVideo.bitrate} kbps</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Match Link - Read-only display */}
          {!isAssetView && activeVideo && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Match Link</h3>
              <div className="space-y-3">
                {loadingMatch ? (
                  <p className="text-sm text-gray-500">Loading match information...</p>
                ) : linkedMatch ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900">{linkedMatch.name}</p>
                        {linkedMatch.name.includes('(Pending)') && (
                          <p className="text-xs text-blue-700 mt-1">
                            Video will be linked after transcoding completes
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          const matchUrl = `/o/${orgId}/score/${linkedMatch.id}`;
                          window.open(matchUrl, '_blank');
                        }}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        View Match →
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">This video is not linked to any match.</p>
                )}
                <p className="text-xs text-gray-500">
                  To link or unlink this video to a match, use the Edit Video option.
                </p>
              </div>
            </div>
          )}

          {/* Upload Actions - Show when video needs file upload */}
          {!isAssetView && activeVideo && shouldShowUploadSection() && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Upload File</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                {activeVideo.status === 'uploaded' ? (
                  <p className="text-sm text-blue-800">
                    This video is waiting for the source file to be uploaded. You can upload files of any size (including 10GB+).
                  </p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-blue-900">
                      File Upload Required
                    </p>
                    <p className="text-sm text-blue-800">
                      The worker could not find the source file. All retry attempts have been exhausted. Please upload the file to proceed with processing.
                    </p>
                  </div>
                )}
                <button
                  onClick={() => {
                    const uploadUrl = `/o/${orgId}/capture/upload/${activeVideo.id}`;
                    window.open(uploadUrl, '_blank');
                  }}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span>Upload File (Opens in New Tab)</span>
                </button>
                <div className="pt-3 border-t border-blue-200">
                  <label className="text-xs text-blue-700 font-medium mb-1 block">R2 Storage Path (for manual upload)</label>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-blue-900 font-mono flex-1 truncate bg-white px-2 py-1 rounded border border-blue-300">
                      {activeVideo.storage_path}
                    </p>
                    <button
                      onClick={() => copyToClipboard(activeVideo.storage_path, 'upload_storage_path')}
                      className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 border border-blue-300 rounded hover:bg-blue-50"
                    >
                      {copiedField === 'upload_storage_path' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    You can also manually upload to this path in the R2 dashboard. Workers will automatically find and process the file.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Storage Info */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Storage Information</h3>
            <div className="space-y-3">
              {isAssetView ? (
                <>
                  <div>
                    <label className="text-xs text-gray-500">Storage Path</label>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-sm text-gray-900 font-mono flex-1 truncate">
                        {assetMetadata?.storagePath}
                      </p>
                      {assetMetadata?.storagePath && (
                        <button
                          onClick={() =>
                            copyToClipboard(assetMetadata.storagePath, 'storage_path')
                          }
                          className="text-blue-600 hover:text-blue-700 text-xs"
                        >
                          {copiedField === 'storage_path' ? 'Copied!' : 'Copy'}
                        </button>
                      )}
                    </div>
                  </div>
                  {assetMetadata?.publicUrl && (
                    <div>
                      <label className="text-xs text-gray-500">Public URL</label>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-sm text-gray-900 font-mono flex-1 truncate">
                          {assetMetadata.publicUrl}
                        </p>
                        <button
                          onClick={() =>
                            copyToClipboard(assetMetadata.publicUrl || '', 'public_url')
                          }
                          className="text-blue-600 hover:text-blue-700 text-xs"
                        >
                          {copiedField === 'public_url' ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <label className="text-xs text-gray-500">Video ID</label>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-sm text-gray-900 font-mono flex-1 truncate">
                        {activeVideo?.id || '—'}
                      </p>
                      {activeVideo?.id && (
                        <button
                          onClick={() => copyToClipboard(activeVideo.id, 'video_id')}
                          className="text-blue-600 hover:text-blue-700 text-xs"
                        >
                          {copiedField === 'video_id' ? 'Copied!' : 'Copy'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Storage Path</label>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-sm text-gray-900 font-mono flex-1 truncate">
                        {activeVideo?.storage_path}
                      </p>
                      {activeVideo?.storage_path && (
                        <button
                          onClick={() =>
                            copyToClipboard(activeVideo.storage_path, 'storage_path')
                          }
                          className="text-blue-600 hover:text-blue-700 text-xs"
                        >
                          {copiedField === 'storage_path' ? 'Copied!' : 'Copy'}
                        </button>
                      )}
                    </div>
                  </div>
                  {activeVideo?.public_url && (
                    <div>
                      <label className="text-xs text-gray-500">Public URL</label>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-sm text-gray-900 font-mono flex-1 truncate">
                          {activeVideo.public_url}
                        </p>
                        <button
                          onClick={() =>
                            copyToClipboard(activeVideo.public_url || '', 'public_url')
                          }
                          className="text-blue-600 hover:text-blue-700 text-xs"
                        >
                          {copiedField === 'public_url' ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {!isAssetView && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">Processing Jobs</h3>
                {jobSummary.total > 0 && (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>Total: {jobSummary.total}</span>
                    {jobSummary.queued > 0 && <span className="text-gray-400">Queued: {jobSummary.queued}</span>}
                    {jobSummary.running > 0 && <span className="text-blue-600">Running: {jobSummary.running}</span>}
                    {jobSummary.succeeded > 0 && <span className="text-green-600">Done: {jobSummary.succeeded}</span>}
                    {jobSummary.failed > 0 && <span className="text-red-600">Failed: {jobSummary.failed}</span>}
                  </div>
                )}
              </div>
              {isLoadingJobs ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500">No processing jobs</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {jobs.map((job) => (
                    <div
                      key={job.id}
                      className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${getJobStatusBadge(job.status)}`}>
                            {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                          </span>
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            {job.job_type.replace('_', ' ')}
                          </span>
                        </div>
                        {job.status === 'failed' && (() => {
                          // Check if there's already a successful job of the same type
                          const hasSuccessfulJobOfSameType = jobs.some(
                            (j) => j.job_type === job.job_type && j.status === 'succeeded' && j.id !== job.id
                          );
                          
                          return hasSuccessfulJobOfSameType ? (
                            <button
                              onClick={() => handleDeleteJob(job.id)}
                              className="text-xs text-red-600 hover:text-red-700"
                            >
                              Delete
                            </button>
                          ) : (
                            <button
                              onClick={() => handleRetryJob(job.id)}
                              className="text-xs text-blue-600 hover:text-blue-700"
                            >
                              Retry
                            </button>
                          );
                        })()}
                      </div>
                      {job.error_message && (
                        <p className="text-xs text-red-600 mt-1">{job.error_message}</p>
                      )}
                      {job.attempts > 0 && (
                        <p className="text-xs text-gray-500 mt-1">Attempts: {job.attempts}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Created: {new Date(job.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          {!isAssetView && activeVideo && (
            <div className="pt-4 border-t border-gray-200 space-y-2">
              {/* Upload Page Link - Show for pending_upload status, but NOT for link type videos */}
              {(() => {
                const processingNotes = activeVideo.processing_notes as Record<string, any> | null;
                const isLinkUpload = !!processingNotes?.source_url;
                return activeVideo.status === 'pending_upload' && !isLinkUpload;
              })() && (
                <button
                  onClick={() => {
                    const uploadUrl = `/o/${orgId}/capture/upload/${activeVideo.id}`;
                    window.open(uploadUrl, '_blank');
                  }}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span>Open Upload Page</span>
                </button>
              )}
              {/* View Full Details button */}
              <button
                onClick={() => {
                  onClose();
                  navigate(`/o/${orgId}/capture/video/${activeVideo.id}`);
                }}
                className="w-full px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                View Full Details
              </button>
              {/* Delete button */}
              {onDeleteVideo && activeVideo.storage_path && (
                <button
                  onClick={() => {
                    onDeleteVideo(activeVideo.id, activeVideo.file_name, activeVideo.storage_path);
                  }}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Video
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoDetailsDrawer;
